import { Alert, Button } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';

import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';

import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Helmet } from 'react-helmet';

import { Poetry, usePoetry } from 'api/poetryClub';
import { Box, CustomBox, GridChild, GridContainer, Title } from 'common';
import { richTextSx } from 'pages/poetry/PoetryPage';
import { PoetryCard } from 'widgets/cards';

import { useLanguage } from '../../context/LanguageContext';
import { poetryClubSinglePageLabels } from '../../locales/translations'

import { PoetryImagesCarousel } from './PoetryImagesCarousel'

export const SinglePoetryPage = () => {
    const { id } = useParams();
    const { onGetAllPoetry, onGetOnePoetry } = usePoetry();

    const [poetryDetails, setPoetryDetails] = useState<Poetry | null>(null);
    const [otherPoetry, setOtherPoetry] = useState<Poetry[]>([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const { language } = useLanguage();
    const otherMeetings = poetryClubSinglePageLabels.otherMeetings[language]
    const allMeetingsButton = poetryClubSinglePageLabels.allMeetingsButton[language]
    const notFoundLabel = poetryClubSinglePageLabels.notFound[language]

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        window.scrollTo(0, 0);

        (async () => {
            const [detailsResult, listResult] = await Promise.all([
                onGetOnePoetry(Number(id)),
                onGetAllPoetry()
            ]);
            if (cancelled) return;

            setPoetryDetails(detailsResult.success ? detailsResult.data : null);
            setNotFound(!detailsResult.success);

            if (listResult.success) {
                const sortedPoetry = listResult.data
                    .filter((a: Poetry) => a.show && a.id !== Number(id))
                    .sort((a: Poetry, b: Poetry) =>
                        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                    );
                setOtherPoetry(sortedPoetry);
            }

            setLoading(false);
        })();

        return () => {
            cancelled = true;
        };
    }, [id, onGetOnePoetry, onGetAllPoetry]);

    if (loading)
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', paddingY: 4 }}>
                <CircularProgress />
            </Box>
        );

    const title = poetryDetails
        ? poetryDetails[`title_${language}`] || poetryDetails.title
        : notFoundLabel;
    const description = poetryDetails
        ? poetryDetails[`description_${language}`] || poetryDetails.description
        : '';

    return (
        <Box>
            <GridContainer>
                <GridChild md={8}>
                    <CustomBox title={title}>
                        <Helmet>
                            <title>{`${title} - AlmaU Open Library`}</title>
                            <meta name="description" content={title} />
                        </Helmet>
                        {notFound || !poetryDetails ? (
                            <Alert severity="warning">{notFoundLabel}</Alert>
                        ) : (
                            <>
                                {poetryDetails.images && poetryDetails.images.length > 0 && poetryDetails.images[0] ? (
                                    <PoetryImagesCarousel poetryItem={poetryDetails} title={title} />
                                ) : (<ImageNotSupportedIcon
                                    sx={{
                                        height: 200,
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '5rem'
                                    }}
                                />)}
                                <Title gutterBottom variant="h5" sx={{ fontWeight: 600, marginTop: 2 }}>
                                    {title}
                                </Title>
                                <Title gutterBottom variant="body2" color="text.secondary" component="div" sx={richTextSx}>
                                    <div dangerouslySetInnerHTML={{ __html: description }} />
                                </Title>
                                <Title variant="body2" color="text.primary">
                                    {dayjs(poetryDetails.created_at).format('DD.MM.YYYY')}
                                </Title>
                            </>
                        )}
                    </CustomBox>
                </GridChild>

                <GridChild md={4}>
                    <CustomBox title={otherMeetings}>
                        {otherPoetry.slice(0, 3).map((poetryItem) => (
                            <PoetryCard key={poetryItem.id} poetryItem={poetryItem} />
                        ))}

                        <Box sx={{ paddingTop: 6, display: 'flex', justifyContent: 'start' }}>
                            <Button variant="contained" color="error" component={Link} to="/poetry-club">
                                {allMeetingsButton}
                            </Button>
                        </Box>
                    </CustomBox>
                </GridChild>
            </GridContainer>
        </Box>
    );
};
